import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import {
  getMyOrganisations,
  updateGithubInstallation,
} from "../services/storageService";

const GithubCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("Connecting your GitHub account...");

  useEffect(() => {
    const handleCallback = async () => {
      const installationId = searchParams.get("installation_id");
      const setupAction = searchParams.get("setup_action");
      const state = searchParams.get("state");
      const orgId = state || localStorage.getItem("pending_gh_install_org_id");

      if (!installationId || !orgId) {
        setStatus("error");
        setMessage("Missing installation information. Please try again.");
        return;
      }

      try {
        const token = await getToken();

        // Update the organisation with the installation ID
        await updateGithubInstallation(
          orgId,
          installationId,
          token || undefined,
        );

        localStorage.removeItem("pending_gh_install_org_id");
        setStatus("success");
        setMessage("GitHub successfully connected!");

        // Fetch the updated org to get the slug for redirect
        const orgs = await getMyOrganisations(token || undefined);
        const org = orgs.find((o) => o.id === orgId);

        setTimeout(() => {
          if (org) {
            navigate(`/${org.slug}/workspace?activeTab=integrations`, {
              replace: true,
            });
          } else {
            navigate("/dashboard", { replace: true });
          }
        }, 1500);
      } catch (error) {
        console.error("Failed to update GitHub installation:", error);
        setStatus("error");
        setMessage("Failed to connect GitHub. Please try again from settings.");
      }
    };

    handleCallback();
  }, [searchParams, getToken, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-8 text-center">
        {status === "loading" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
            <p className="text-slate-600 dark:text-slate-400 font-medium">
              {message}
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="flex flex-col items-center gap-4 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <CheckCircle size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Success!
            </h2>
            <p className="text-slate-600 dark:text-slate-400">{message}</p>
            <p className="text-xs text-slate-400 mt-2">
              Redirecting you back...
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center gap-4 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center text-red-600 dark:text-red-400">
              <AlertCircle size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Connection Failed
            </h2>
            <p className="text-slate-600 dark:text-slate-400">{message}</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GithubCallback;
